import styled, { css } from 'styled-components';

import { media } from '@savgroup-front-common/constants/src/shared';

import { rem } from '../../helpers';

import { TABLE_ALIGN, TABLE_ORDER } from './TableContent.types';

export const $StyledTable = styled.table`
  min-width: 100%;
  border-collapse: collapse;
`;

export const $TableContainer = styled.div`
  overflow-x: auto;
  flex: 1;
  margin-bottom: 1.5rem;
`;

interface CellMixinProps {
  disableSortBy?: boolean;
  align?: TABLE_ALIGN | string;
  $isNewUiEnabled?: boolean;
  order?: TABLE_ORDER;
}

const cellMixin = css<CellMixinProps>`
  padding: ${({ $isNewUiEnabled }) => ($isNewUiEnabled ? '10px 8px' : rem(10))};
  text-align: ${({ align }) => align || TABLE_ALIGN.LEFT};
  font-size: ${({ theme, $isNewUiEnabled }) =>
    $isNewUiEnabled ? theme.fonts.size.megaSmall : theme.fonts.size.ultraSmall};
  line-height: 1.1;
  &:first-child {
    padding-left: ${({ $isNewUiEnabled }) => ($isNewUiEnabled ? '8px' : 0)};
  }
  &:last-child {
    padding-right: ${({ $isNewUiEnabled }) => ($isNewUiEnabled ? '8px' : 0)};
  }
`;

export const $CellContent = styled.div`
  display: inline-block;
`;

export const $StyledTd = styled.td<CellMixinProps>`
  ${cellMixin};
  font-weight: ${({ theme }) => theme.fonts.weight.normal};
  color: ${({ theme, $isNewUiEnabled }) =>
    $isNewUiEnabled
      ? theme.newUI.defaultColors.text
      : theme.colors.mainTextColor};
  @media ${media.minWidth.lg} {
    font-size: ${({ theme, $isNewUiEnabled }) =>
      $isNewUiEnabled
        ? theme.fonts.size.small
        : theme.fonts.size.medium} !important;
  }
`;

const thHover = css`
  &:hover {
    background-color: ${({ theme }) => theme.newUI.variationColors.light5};
  }
`;

export const $StyledTh = styled.th<CellMixinProps>`
  ${cellMixin}

  ${({ disableSortBy }) => (disableSortBy ? null : thHover)}
  & {
    background-color: ${({ theme, order }) =>
      order === TABLE_ORDER.ASC || order === TABLE_ORDER.DESC
        ? theme.newUI.variationColors.light4
        : undefined};
  }
  & svg {
    color: ${({ theme }) => theme.colors.primary};
  }

  ${({ disableSortBy, order }) => {
    if (!disableSortBy && !!order) {
      return css`
        background-color: ${({ theme }) => theme.newUI.variationColors.light5};

        & * {
          font-weight: ${({ theme }) => theme.fonts.weight.bold};
        }
      `;
    }
  }}

  border-bottom: ${({ theme }) => theme.newUI.inputBorderColors.default} 1px
    solid;
  padding-bottom: ${rem(10)};
  user-select: none;
  font-weight: ${({ theme }) => theme.fonts.weight.mediumFont};
  color: ${({ theme, $isNewUiEnabled }) =>
    $isNewUiEnabled
      ? theme.newUI.defaultColors.secondaryText
      : theme.colors.paragraphTextColor};
  white-space: nowrap;
  > span {
    white-space: break-spaces;
  }

  & > div {
    display: flex;
    align-items: center;
  }
`;

export const $PaginationContainer = styled.div`
  padding-top: 1rem;
`;

export const $SearchContainer = styled.div`
  margin-bottom: 1rem;
`;

export const $StyledTr = styled.tr<{
  $isEven?: boolean;
  $isNewUiEnabled?: boolean;
}>`
  position: relative;
  border-collapse: collapse;

  background-color: ${({ theme, $isEven, $isNewUiEnabled }) =>
    $isEven && !$isNewUiEnabled ? theme.systemColors.defaultBackground : null};
`;
export const $HeaderTr = styled.tr`
  position: relative;
  border-collapse: collapse;
`;

export const $StyledTHead = styled.thead`
  ${$StyledTr} {
    vertical-align: bottom;
  }
`;

export const $StyledTFoot = styled.tfoot`
  ${$StyledTr} {
    vertical-align: bottom;
    border-top: ${({ theme }) => theme.newUI.inputBorderColors.default} 1px
      solid;
    ${$StyledTd} {
      font-weight: ${({ theme }) => theme.fonts.weight.bold};
      color: ${({ theme }) => theme.colors.mainTextColor};
      font-size: ${({ theme }) => theme.fonts.size.small}!important;
    }
  }
`;

export const $StyledTBody = styled.tbody<{ $isNewUiEnabled?: boolean }>`
  ${$StyledTr} {
    vertical-align: top;
    :not(:first-child) ${$StyledTd} {
      border-top: ${({ theme }) => theme.newUI.inputBorderColors.default} 1px
        solid;
    }
    &:hover {
      background: ${({ theme }) =>
        theme.colors.datatableRowBackgroundHoverColor};
    }
  }
`;
